import type { User } from "@/domain/user"
import type { Services } from "@/interfaces/services"

export type InitialParamsIntercomAdapter = {
  appId: string
  isDev: boolean
  isSelfHosted: boolean
  logger: Services.ILoggerAdapter
}

export class IntercomAdapter {
  private appId: string
  private isDev: boolean
  private logger: Services.ILoggerAdapter
  private isSelfHosted: boolean

  constructor({
    appId,
    logger,
    isDev,
    isSelfHosted
  }: InitialParamsIntercomAdapter) {
    this.appId = appId
    this.isDev = isDev
    this.isSelfHosted = isSelfHosted
    this.logger = logger
    this.shutdown()
  }

  private hideDefaultLauncher(value: boolean) {
    this.api("update", { hide_default_launcher: value })
  }

  logError(err: unknown, target: string) {
    try {
      const message = err instanceof Error ? err : `Error on ${target}`

      this.logger.error(message, { target })
    } catch (error) {
      this.logger?.error?.(error as Error, {
        target: "IntercomAdapter.logError"
      })
    }
  }

  api(method: string, ...args: unknown[]) {
    try {
      if (this.isDev || this.isSelfHosted) return

      if ((window as any).Intercom) {
        return (window as any).Intercom.apply(null, [method, ...args])
      }

      this.logError(new Error("Intercom not found"), "IntercomAdapter.api")
    } catch (err) {
      this.logError(err, "IntercomAdapter.api")
    }
  }

  boot() {
    try {
      this.api("boot", {
        app_id: this.appId,
        z_index: 1050
      })
    } catch (err) {
      this.logError(err, "IntercomAdapter.boot")
    }
  }

  update(user: User) {
    try {
      const { fullName, email, company } = user

      this.api("update", {
        email,
        company,
        app_id: this.appId,
        created_at: Date.now(),
        name: fullName || email,
        user_id: email
      })
    } catch (err) {
      this.logError(err, "IntercomAdapter.update")
    }
  }

  hideLauncher() {
    this.hideDefaultLauncher(true)
  }

  showLauncher() {
    this.hideDefaultLauncher(false)
  }

  shutdown() {
    try {
      this.api("shutdown")
      this.boot()
    } catch (err) {
      this.logError(err, "IntercomAdapter.shutdown")
    }
  }
}
